import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseclient";
import { useNavigate } from "react-router-dom";
import { createUserRecord } from "../utils/authHelpers";

function CompleteProfile() {
  const [fullName, setFullName] = useState("");
  const [city, setCity] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  // Ensure users table row exists when landing here after OAuth; redirect if profile already complete
  useEffect(() => {
    const ensureUserRowAndMaybeRedirect = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await createUserRecord(user);
      const { data: profile } = await supabase
        .from("users")
        .select("profile_created")
        .eq("id", user.id)
        .single();
      if (profile?.profile_created) {
        navigate("/jobs", { replace: true });
      }
    };
    ensureUserRowAndMaybeRedirect();
  }, [navigate]);

  const handleFileChange = (e) => {
    setResumeFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);


    // Get current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      alert("User not found. Please sign in again.");
      return;
    }

    let resumeUrl = null;

    if (resumeFile) {
      const fileExt = resumeFile.name.split(".").pop();
      const filePath = `${user.id}/resume.${fileExt}`;

      // Try upload with regular client (should work with proper RLS policies)
      const { error: uploadError } = await supabase.storage
        .from("resumes")
        .upload(filePath, resumeFile, { upsert: true });

      if (uploadError) {
        console.error("❌ Upload failed:", uploadError.message);
        alert("Failed to upload resume: " + uploadError.message);
        setUploading(false);
        return;
      }

      // Get the public URL
      const { data: urlData, error: urlError } = supabase.storage
        .from("resumes")
        .getPublicUrl(filePath);

      if (urlError) {
        console.error("Failed to get public URL:", urlError.message);
      }

      resumeUrl = urlData?.publicUrl;
    }

    // Save to users table
    const { data: sessionData } =
      await supabase.auth.getSession();

    let authUserId;

    if (sessionData?.session?.user) {
      authUserId = sessionData.session.user.id;
    } else {
      // Fallback to getUser (in case session is empty)
      const { data: userData } =
        await supabase.auth.getUser();
      if (userData?.user?.id) {
        authUserId = userData.user.id;
      } else {
        alert("Could not retrieve user session.");
        setUploading(false);
        return;
      }
    }

    // Check if user exists in users table (OAuth may not have created a row)
    const { error: userCheckError } = await supabase
      .from("users")
      .select("*")
      .eq("id", authUserId)
      .single();

    if (userCheckError) {
      // No row yet (e.g. Google OAuth doesn't run signup flow) — create it
      const { error: insertError } = await supabase.from("users").insert({
        id: authUserId,
        email: user.email ?? "",
        profile_created: false,
      });
      if (insertError) {
        console.error(
          "❌ User not in users table and insert failed:",
          insertError.message
        );
        alert("Failed to update profile. Please contact support or try again later.");
        setUploading(false);
        return;
      }
    }

    const { error: updateError } = await supabase
      .from("users")
      .update({
        full_name: fullName,
        city: city,
        resume_url: resumeUrl,
        profile_created: true,
      })
      .eq("id", authUserId)
      .select();

    if (updateError) {
      console.error("❌ RLS update error:", updateError.message);
      alert("Failed to update profile: " + updateError.message);
      setUploading(false);
      return;
    }

    // Done — redirect to home or dashboard
    navigate("/jobs");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          borderBottom: "1px solid #e9ecef",
          padding: "1rem 0",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            padding: "0 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1
            style={{
              color: "#2c3e50",
              fontSize: "1.8rem",
              fontWeight: "600",
              margin: 0,
            }}
          >
            ✏️ Complete Your Profile
          </h1>
          <button
            onClick={() => navigate("/")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "0.9rem",
            }}
          >
            Skip for Now
          </button>
        </div>
      </div>

      <div
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          padding: "0 2rem 2rem",
        }}
      >
        {/* Welcome Message */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e9ecef",
            marginBottom: "2rem",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "3rem",
              marginBottom: "1rem",
            }}
          >
            🎯
          </div>
          <h2
            style={{
              color: "#2c3e50",
              fontSize: "1.4rem",
              fontWeight: "600",
              marginBottom: "0.5rem",
            }}
          >
            Welcome to ChapterOne!
          </h2>
          <p
            style={{
              color: "#6c757d",
              fontSize: "1rem",
              lineHeight: "1.5",
              margin: 0,
            }}
          >
            Complete your profile to get started with finding entry-level jobs
            in California.
          </p>
        </div>

        {/* Profile Form */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e9ecef",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  color: "#2c3e50",
                  fontSize: "1rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                Full Name *
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Enter your full name"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                onBlur={(e) => (e.target.style.borderColor = "#e9ecef")}
              />
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  color: "#2c3e50",
                  fontSize: "1rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                City
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter your city (e.g., San Francisco, Los Angeles)"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                onBlur={(e) => (e.target.style.borderColor = "#e9ecef")}
              />
              <p
                style={{
                  color: "#6c757d",
                  fontSize: "0.85rem",
                  margin: "0.5rem 0 0 0",
                }}
              >
                This helps us show you relevant local opportunities
              </p>
            </div>

            <div style={{ marginBottom: "2rem" }}>
              <label
                style={{
                  display: "block",
                  color: "#2c3e50",
                  fontSize: "1rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                Resume (PDF)
              </label>
              <div
                style={{
                  border: "2px dashed #e9ecef",
                  borderRadius: "6px",
                  padding: "1.5rem",
                  textAlign: "center",
                  backgroundColor: "#f8f9fa",
                  transition: "border-color 0.2s ease",
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  📄
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  style={{
                    display: "none",
                  }}
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  style={{
                    display: "inline-block",
                    backgroundColor: "#007bff",
                    color: "white",
                    padding: "0.75rem 1.5rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  Choose Resume File
                </label>
                {resumeFile && (
                  <div
                    style={{
                      color: "#27ae60",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                    }}
                  >
                    ✅ {resumeFile.name}
                  </div>
                )}
                <p
                  style={{
                    color: "#6c757d",
                    fontSize: "0.85rem",
                    margin: "0.5rem 0 0 0",
                  }}
                >
                  Upload your resume in PDF format (optional)
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading}
              style={{
                width: "100%",
                padding: "1rem",
                backgroundColor: uploading ? "#6c757d" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: uploading ? "not-allowed" : "pointer",
                fontWeight: "600",
                fontSize: "1rem",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!uploading) {
                  e.target.style.backgroundColor = "#0056b3";
                }
              }}
              onMouseLeave={(e) => {
                if (!uploading) {
                  e.target.style.backgroundColor = "#007bff";
                }
              }}
            >
              {uploading ? "Creating Profile..." : "Complete Profile"}
            </button>
          </form>
        </div>

        {/* Help Text */}
        <div
          style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #bbdefb",
            borderRadius: "6px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <p
            style={{
              color: "#1976d2",
              margin: 0,
              fontSize: "0.9rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            💡 <strong>Tip:</strong> You can always update your profile later
            from your account settings.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CompleteProfile;
